import React from 'react';
import { Box, Grid, Typography, Button, TextField, Link, IconButton } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import XIcon from '@mui/icons-material/X';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useT } from "contexts/TranslationContext";

const PRIMARY_GREEN = '#34986C'; 
const LIGHT_BG = '#f8fbf5'; // Very light background color

// --- Contact Form Component ---
const ContactForm = () => {
    const t = useT();
    const handleSubmit = (event) => {
        event.preventDefault();
        // Placeholder for form submission logic
        console.log('Form submitted!');
    };

    return (
        <Box 
            component="form" 
            onSubmit={handleSubmit} 
            noValidate 
            sx={{ 
                width: '100%', 
                maxWidth: { xs: '100%', md: '450px' },
            }}
        >
            <TextField
                required
                fullWidth
                id="email"
                label={t("contact.email")}
                name="email"
                autoComplete="email"
                variant="outlined"
                margin="normal"
                sx={{ mb: 2, bgcolor: 'white', borderRadius: '4px' }}
            />
            <TextField
                required
                fullWidth
                id="fullName"
                label={t("contact.name")}
                name="fullName"
                autoComplete="name"
                variant="outlined"
                margin="normal"
                sx={{ mb: 2, bgcolor: 'white', borderRadius: '4px' }}
            />
            <TextField
                required
                fullWidth
                id="comments"
                label={t("contact.message")}
                name="comments"
                multiline
                rows={6}
                variant="outlined"
                margin="normal"
                sx={{ mb: 4, bgcolor: 'white', borderRadius: '4px' }}
            />
            <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                    bgcolor: PRIMARY_GREEN,
                    '&:hover': {
                        bgcolor: '#2d845e',
                    },
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: 3,
                }}
            >
                {t("contact.submit")}
            </Button>
        </Box>
    );
};

const ContactInfo = () => {
    const t = useT();
    const contactDetails = [
        {
            Icon: EmailIcon,
            text: "support@chronotrack.ee",
            isLink: true,
            href: "mailto:support@chronotrack.ee",
        },
    ];

    return (
        <Box sx={{ 
            textAlign: { xs: 'center', md: 'left' },
            pr: { md: 4 }
        }}>
            <Box mb={6}>
                {contactDetails.map((detail, index) => (
                    <Box key={index} display="flex" alignItems="center" justifyContent={{ xs: 'center', md: 'flex-start' }} mb={2}>
                        <Box sx={{ color: PRIMARY_GREEN, mr: 2, width: 20 }}>
                            <detail.Icon />
                        </Box>
                        {detail.isLink ? (
                            <Link 
                                href={detail.href} 
                                color="text.primary" 
                                underline="hover"
                                sx={{ 
                                    fontWeight: 500, 
                                    fontSize: '1.125rem',
                                    color: 'grey.900' 
                                }}
                            >
                                {detail.text}
                            </Link>
                        ) : (
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    fontWeight: 500, 
                                    fontSize: '1.125rem',
                                    color: 'grey.900' 
                                }}
                            >
                                {detail.text}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const ContactPage = () => {
    const t = useT();
    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'white', 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Inter, sans-serif',
                py: { xs: 8, md: 12 },
                px: { xs: 4, sm: 8, lg: 16 },
            }}
        >
            <Box 
                maxWidth="lg" 
                width="100%" 
                mx="auto"
                textAlign="center"
                mb={{ xs: 6, md: 10 }}
            >
                <Typography
                    id="contact"
                    variant="h3"
                    component="h1"
                    sx={{
                        fontWeight: 800,
                        color: 'grey.900',
                        mb: 2,
                        fontSize: { xs: '2.5rem', sm: '3rem' }
                    }}
                >
                    {t("contact.title")}
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: 'grey.700',
                        maxWidth: 600,
                        mx: 'auto',
                    }}
                >
                    {t("contact.subtitle")}
                </Typography>
            </Box>

            <Grid 
                container 
                maxWidth="lg" 
                mx="auto" 
                spacing={{ xs: 6, md: 10 }} 
                justifyContent="center"
                alignItems="flex-start" 
            >
                <Grid item xs={12} md={5}>
                    <ContactInfo />
                </Grid>
                <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <ContactForm />
                </Grid>
            </Grid>
        </Box>
    );
};

export default ContactPage;
